import { supabase } from "@/lib/supabase";
import { Database } from "@/lib/database.types";

type AIAgent = Database["public"]["Tables"]["ai_agents"]["Row"];
type FeatureUsage = Database["public"]["Tables"]["feature_usage"]["Row"];

export interface AIAgentWithStats extends AIAgent {
  usage_count: number;
  last_used: string | null;
  maintenance_status: "active" | "maintenance" | "disabled";
  maintenance_message?: string;
  daily_usage_limit?: number;
  current_daily_usage?: number;
}

export interface AgentUsageStats {
  total_usage: number;
  daily_usage: number;
  weekly_usage: number;
  monthly_usage: number;
  last_used: string | null;
  usage_trend: "increasing" | "decreasing" | "stable";
}

export class AIAgentsService {
  /**
   * Get all AI agents with usage statistics for a user
   */
  static async getUserAgentsWithStats(
    userId: string
  ): Promise<AIAgentWithStats[]> {
    try {
      // Get all AI agents
      const { data: agents, error: agentsError } = await supabase
        .from("ai_agents")
        .select("*")
        .eq("is_visible", true)
        .order("name");

      if (agentsError) {
        throw new Error(`Error fetching agents: ${agentsError.message}`);
      }

      if (!agents) return [];

      // Get usage statistics for each agent
      const agentsWithStats = await Promise.all(
        agents.map(async (agent) => {
          const stats = await this.getAgentUsageStats(userId, agent.id);
          const dailyUsage = await this.getDailyUsageLimit(userId, agent.id);

          return {
            ...agent,
            usage_count: stats.total_usage,
            last_used: stats.last_used,
            maintenance_status: this.getMaintenanceStatus(agent.id),
            maintenance_message: this.getMaintenanceMessage(agent.id),
            daily_usage_limit: dailyUsage.limit,
            current_daily_usage: dailyUsage.current,
          };
        })
      );

      return agentsWithStats;
    } catch (error) {
      console.error("Error in getUserAgentsWithStats:", error);
      throw error;
    }
  }

  /**
   * Get usage statistics for a specific agent
   */
  static async getAgentUsageStats(
    userId: string,
    agentId: string
  ): Promise<AgentUsageStats> {
    try {
      // Get all usage records for this agent and user
      const { data: usage, error: usageError } = await supabase
        .from("feature_usage")
        .select("*")
        .eq("user_id", userId)
        .eq("feature_id", agentId)
        .order("used_at", { ascending: false });

      if (usageError) {
        throw new Error(`Error fetching usage: ${usageError.message}`);
      }

      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const totalUsage = usage?.length || 0;
      const dailyUsage =
        usage?.filter((u) => new Date(u.used_at) >= oneDayAgo).length || 0;
      const weeklyUsage =
        usage?.filter((u) => new Date(u.used_at) >= oneWeekAgo).length || 0;
      const monthlyUsage =
        usage?.filter((u) => new Date(u.used_at) >= oneMonthAgo).length || 0;

      const lastUsed = usage && usage.length > 0 ? usage[0].used_at : null;

      // Calculate usage trend (simple comparison with previous period)
      const previousWeekUsage =
        usage?.filter((u) => {
          const usageDate = new Date(u.used_at);
          return (
            usageDate >= new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000) &&
            usageDate < oneWeekAgo
          );
        }).length || 0;

      let usageTrend: "increasing" | "decreasing" | "stable" = "stable";
      if (weeklyUsage > previousWeekUsage) {
        usageTrend = "increasing";
      } else if (weeklyUsage < previousWeekUsage) {
        usageTrend = "decreasing";
      }

      return {
        total_usage: totalUsage,
        daily_usage: dailyUsage,
        weekly_usage: weeklyUsage,
        monthly_usage: monthlyUsage,
        last_used: lastUsed,
        usage_trend: usageTrend,
      };
    } catch (error) {
      console.error("Error in getAgentUsageStats:", error);
      return {
        total_usage: 0,
        daily_usage: 0,
        weekly_usage: 0,
        monthly_usage: 0,
        last_used: null,
        usage_trend: "stable",
      };
    }
  }

  /**
   * Get daily usage limit information for an agent
   */
  static async getDailyUsageLimit(
    userId: string,
    agentId: string
  ): Promise<{ limit: number; current: number }> {
    try {
      const { data, error } = await supabase
        .from("daily_usage_limits")
        .select("daily_limit, current_usage")
        .eq("user_id", userId)
        .eq("feature_id", agentId)
        .eq("usage_date", new Date().toISOString().split("T")[0])
        .maybeSingle();

      if (error) {
        console.error("Error fetching daily usage limit:", error);
      }

      return {
        limit: data?.daily_limit || 10,
        current: data?.current_usage || 0,
      };
    } catch (error) {
      console.error("Error in getDailyUsageLimit:", error);
      return { limit: 10, current: 0 };
    }
  }

  /**
   * Track usage of an AI agent
   */
  static async trackAgentUsage(
    userId: string,
    agentId: string,
    metadata: Record<string, unknown> = {}
  ): Promise<void> {
    try {
      const { error } = await supabase.from("feature_usage").insert({
        user_id: userId,
        feature_id: agentId,
        metadata,
      });

      if (error) {
        console.error("Error tracking agent usage:", error);
      }
    } catch (error) {
      console.error("Error in trackAgentUsage:", error);
    }
  }

  /**
   * Get maintenance status for an agent (hardcoded for now, could be moved to database)
   */
  private static getMaintenanceStatus(
    agentId: string
  ): "active" | "maintenance" | "disabled" {
    // This could be moved to a database table for dynamic management
    const maintenanceConfig: Record<
      string,
      "active" | "maintenance" | "disabled"
    > = {
      "contract-review": "active",
      "legal-research": "active",
      "compliance-monitor": "maintenance", // Example: under maintenance
      "legal-chatbot": "active",
      "litigation-predictor": "active",
      "ip-assistant": "active",
      "document-generator": "active",
      "document-drafting-v2": "active",
      "compliance-ethics": "disabled", // Example: disabled
      "debt-collection": "active",
      "consumer-assistant": "active",
    };

    return maintenanceConfig[agentId] || "active";
  }

  /**
   * Get maintenance message for an agent
   */
  private static getMaintenanceMessage(agentId: string): string | undefined {
    const maintenanceMessages: Record<string, string> = {
      "compliance-monitor":
        "En mantenimiento programado. Estará disponible el 15 de diciembre.",
      "compliance-ethics":
        "Temporalmente deshabilitado por actualizaciones de seguridad.",
    };

    return maintenanceMessages[agentId];
  }

  /**
   * Get most used agents for a user
   */
  static async getMostUsedAgents(
    userId: string,
    limit: number = 5
  ): Promise<AIAgentWithStats[]> {
    try {
      const agents = await this.getUserAgentsWithStats(userId);
      return agents
        .sort((a, b) => b.usage_count - a.usage_count)
        .slice(0, limit);
    } catch (error) {
      console.error("Error in getMostUsedAgents:", error);
      return [];
    }
  }

  /**
   * Get recently used agents for a user
   */
  static async getRecentlyUsedAgents(
    userId: string,
    limit: number = 5
  ): Promise<AIAgentWithStats[]> {
    try {
      const agents = await this.getUserAgentsWithStats(userId);
      return agents
        .filter((agent) => agent.last_used)
        .sort((a, b) => {
          if (!a.last_used) return 1;
          if (!b.last_used) return -1;
          return (
            new Date(b.last_used).getTime() - new Date(a.last_used).getTime()
          );
        })
        .slice(0, limit);
    } catch (error) {
      console.error("Error in getRecentlyUsedAgents:", error);
      return [];
    }
  }
}
